<template>
  <div class="goods-list">
    <van-nav-bar :title="shopName" left-arrow @click-left="$router.back()" />

    <!-- 搜索框 -->
    <div class="search-container">
      <van-search
        v-model="searchKeyword"
        placeholder="搜索商品"
        shape="round"
        @input="onSearchInput"
        @clear="onSearchClear"
        clearable
      />
    </div>

    <div class="goods-container">
      <div
        v-for="(item, index) in goodsList"
        :key="`${item.goodsId}-${index}`"
        class="goods-item"
        @click="handleItemClick(item)"
      >
        <div class="goods-image">
          <img :src="item.goodsImg" :alt="item.goodsName" />
          <div class="sold-out-mask" v-if="item.isSoldOut">
            <span>已售罄</span>
          </div>
        </div>
        <div class="goods-content">
          <div class="goods-info">
            <h3 class="goods-name">{{ item.goodsName }}</h3>
            <div class="goods-tags">
              <span v-for="tag in item.skuTagDataList" :key="tag" class="tag">{{
                tag
              }}</span>
            </div>
            <div class="goods-desc" v-html="item.customTag"></div>
          </div>
          <div class="goods-action">
            <van-button
              type="primary"
              size="small"
              round
              class="add-btn"
              :disabled="item.isSoldOut"
            >
              <van-icon name="plus" />
            </van-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 商品详情弹出层 -->
    <van-popup
      v-model="showDetail"
      position="bottom"
      round
      closeable
      close-icon="close"
      :style="{ height: '85%' }"
    >
      <div class="detail-popup" v-if="goodsDetail">
        <div class="scrollable-content">
          <!-- 商品主图 -->
          <div class="goods-image">
            <img :src="goodsDetail.goodsImg" :alt="goodsDetail.goodsName" />
          </div>

          <!-- 商品信息 -->
          <div class="goods-info">
            <h1 class="goods-name">{{ goodsDetail.goodsName }}</h1>
            <div class="goods-tags">
              <span
                v-for="tag in goodsDetail.skuTagDataList"
                :key="tag"
                class="tag"
                >{{ tag }}</span
              >
            </div>
            <div class="goods-desc" v-html="goodsDetail.customTag"></div>
            <div
              v-if="goodsDetail.unitList && goodsDetail.unitList.length"
              class="unit-list"
            >
              <div
                v-for="unit in goodsDetail.unitList"
                :key="unit.unitId"
                class="unit-item"
                :class="{ active: unit.isSelect }"
                @click="selectUnit(unit)"
              >
                {{ unit.unitName }}
              </div>
            </div>
          </div>

          <!-- 商品选项 -->
          <template v-if="goodsDetail && goodsDetail.sortedPracticeList">
            <div
              v-for="group in goodsDetail.sortedPracticeList"
              :key="group.practiceName"
              class="specs-section"
            >
              <div class="section-title">{{ group.practiceName }}</div>
              <div class="options-list">
                <div
                  v-for="option in group.practiceValueList"
                  :key="option.practiceValueId"
                  class="option-item"
                  :class="{
                    active:
                      selectedOptions[group.practiceName] ===
                      option.practiceValue,
                  }"
                  @click="
                    selectOption(group.practiceName, option.practiceValue)
                  "
                >
                  {{ option.practiceValue }}
                </div>
              </div>
            </div>
          </template>

          <!-- 底料选项 -->
          <template v-if="goodsDetail && goodsDetail.attachGroups">
            <div
              v-for="group in goodsDetail.attachGroups.filter(g => g.name === '底料')"
              :key="group.id"
              class="specs-section"
            >
              <div class="section-title">{{ group.name }}</div>
              <div class="attach-list">
                <div
                  v-for="attach in group.attaches.filter(a => !a.salePrice || a.salePrice === 0)"
                  :key="attach.attachGoodsId"
                  class="attach-item"
                  :class="{
                    active: isAttachSelected(attach.attachGoodsId),
                    disabled: attach.inventory <= 0
                  }"
                  @click="toggleAttach(attach)"
                >
                  <div class="attach-info">
                    <img 
                      v-if="attach.pictureUrlList && attach.pictureUrlList.length" 
                      :src="attach.pictureUrlList[0]" 
                      class="attach-image"
                    />
                    <span class="attach-name">{{ attach.attachGoodsName }}</span>
                  </div>
                  <div class="attach-price">
                    <span v-if="attach.inventory <= 0" class="sold-out-text">已售罄</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 底部购买栏 -->
        <div class="bottom-bar">
          <van-button
            type="primary"
            block
            @click="goToSettlement"
            :disabled="!isValidSelection"
          >
            结算
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import request from "@/utils/request";
import { mapState } from "vuex";

export default {
  name: "GoodsList",
  data() {
    return {
      shopName: "",
      shopAddress: "", // 1. 添加 shopAddress
      goodsList: [],
      allGoodsList: [], // 存储所有商品数据
      searchKeyword: "", // 搜索关键词
      searchTimer: null, // 搜索防抖定时器
      showDetail: false,
      currentGoods: null,
      goodsDetail: null,
      selectedOptions: {},
      selectedAttaches: [], // 选中的加料列表
    };
  },
  computed: {
    ...mapState(["appConfig", "memberCoupon"]),

    isValidSelection() {
      if (!this.goodsDetail) return false;

      // 1. 检查是否已选择杯型（如果有杯型的话）
      const hasUnitList = this.goodsDetail.unitList && this.goodsDetail.unitList.length > 0;
      const isUnitSelected = !hasUnitList || this.goodsDetail.unitList.some((unit) => unit.isSelect);

      // 2. 检查是否所有必选的口味选项都已选择
      const areOptionsSelected = (
        this.goodsDetail.sortedPracticeList || []
      ).every((group) => {
        if (group.isRequired) {
          // isRequired > 0 表示必选
          return !!this.selectedOptions[group.practiceName];
        }
        return true;
      });

      // 3. 两者都满足时，才算有效选择
      return isUnitSelected && areOptionsSelected;
    },
  },
  created() {
    this.shopName = this.$route.query.shopName || "";
    this.shopAddress = this.$route.query.shopAddress || ""; // 2. 从路由获取 shopAddress
    this.fetchGoodsList();
  },
  methods: {
    async fetchGoodsList() {
      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });
      try {
        const shopId = this.$route.query.shopId;
        if (!shopId || !this.appConfig) {
          this.$toast.fail("缺少店铺ID或应用配置");
          loading.clear();
          return;
        }

        // 检查是否有优惠券信息
        if (!this.$store.state.memberCoupon || !this.$store.state.memberCoupon.couponRuleId) {
          this.$toast.fail("缺少优惠券信息");
          loading.clear();
          return;
        }

        // 1. 先获取店铺完整菜单信息（包含所有商品的详细数据）
        console.log("正在获取店铺菜单信息...");
        const menuResponse = await request.post("/mx/get-menu-info", {
          shopId: shopId,
          orderType: '1',
          accessToken: this.$store.state.mxAccessToken,
        });

        if (!menuResponse || menuResponse.code !== 0 || !menuResponse.data) {
          this.$toast.fail(menuResponse.msg || "获取店铺菜单失败");
          loading.clear();
          return;
        }

        const menuData = menuResponse.data;
        console.log("店铺菜单数据：", menuData);
        console.log("menuData类型：", typeof menuData);
        console.log("menuData是否为数组：", Array.isArray(menuData));
        console.log("menuData长度：", menuData?.length);

        // 从菜单数据中提取所有商品
        const allProducts = [];
        // menuData 直接就是分类列表数组
        if (Array.isArray(menuData) && menuData.length > 0) {
          menuData.forEach((category, index) => {
            console.log(`分类${index}:`, category.categoryNameSale, "商品数量：", category.products?.length);
            if (category.products && category.products.length > 0) {
              category.products.forEach(product => {
                allProducts.push(product);
              });
            }
          });
        }

        console.log("店铺所有商品数量：", allProducts.length);

        // 2. 判断是否为全品券
        // 先检查 memberCoupon 中是否已有 isSuitAllGoods 信息
        const memberCoupon = this.$store.state.memberCoupon;
        let isSuitAllGoods = memberCoupon.isSuitAllGoods === 1;
        let processedGoodsList = [];

        if (isSuitAllGoods) {
          // 全品券：直接显示店铺所有商品，不需要调用优惠券商品接口
          console.log("检测到全品券，展示店铺所有商品");
          
          processedGoodsList = allProducts.map(product => ({
            goodsId: product.productId,
            goodsName: product.productName,
            goodsImg: product.productLogo || '',
            isSoldOut: product.productStatus !== 1,
            skuTagDataList: product.productTags 
              ? product.productTags.map(tag => tag.content) 
              : [],
            customTag: product.productDesc || '',
            type: product.productType || 1,
            productPrice: product.productPrice,
            cups: product.cups || [],
            productSpecs: product.productSpecs || [],
            productAttrs: product.productAttrs || [],
            modifiedTime: product.modifiedTime || '', // 保存商品修改时间
            _hasDetail: true
          }));
          
          console.log("全品券商品列表处理完成，商品数量：", processedGoodsList.length);
        } else {
          // 限制券：需要获取优惠券商品列表
          console.log("检测到限制券，获取指定商品列表");
          
          let listResponse = await request.post("/mx/get-coupon-products", {
            shopId: shopId,
            couponCode: memberCoupon.couponCode,
            orderType: '1',
            accessToken: this.$store.state.mxAccessToken,
          });

          if (!listResponse || listResponse.code !== 0 || !listResponse.data) {
            this.$toast.fail(listResponse.msg || "获取优惠券信息失败");
            this.goodsList = [];
            loading.clear();
            return;
          }

          const responseData = listResponse.data;
          console.log("优惠券商品接口返回数据：", responseData);
          
          // 遍历优惠券商品列表
          processedGoodsList = (responseData.menuProductVos || []).map(couponProduct => {
            // 尝试从店铺菜单中查找对应的商品
            const menuProduct = allProducts.find(
              p => p.productId === couponProduct.productId
            );
            
            if (menuProduct) {
              // 如果在店铺菜单中找到了，使用菜单数据（包含完整的规格信息）
              return {
                goodsId: menuProduct.productId,
                goodsName: menuProduct.productName,
                goodsImg: couponProduct.productLogo || menuProduct.productLogo || '',
                isSoldOut: menuProduct.productStatus !== 1,
                skuTagDataList: couponProduct.productTags 
                  ? couponProduct.productTags.map(tag => tag.content) 
                  : (menuProduct.productTags ? menuProduct.productTags.map(tag => tag.content) : []),
                customTag: couponProduct.productDesc || menuProduct.productDesc || '',
                type: menuProduct.productType || 1,
                productPrice: menuProduct.productPrice,
                cups: menuProduct.cups || [],
                productSpecs: menuProduct.productSpecs || [],
                productAttrs: menuProduct.productAttrs || [],
                modifiedTime: menuProduct.modifiedTime || '', // 保存商品修改时间
                _hasDetail: true
              };
            } else {
              // 如果在店铺菜单中找不到（比如零食类商品），直接使用优惠券数据
              console.log("商品不在店铺菜单中，使用优惠券数据：", couponProduct.productName);
              return {
                goodsId: couponProduct.productId,
                goodsName: couponProduct.productName,
                goodsImg: couponProduct.productLogo || '',
                isSoldOut: couponProduct.productStatus !== 1,
                skuTagDataList: couponProduct.productTags 
                  ? couponProduct.productTags.map(tag => tag.content) 
                  : [],
                customTag: couponProduct.productDesc || '',
                type: couponProduct.productType || 1,
                productPrice: couponProduct.productPrice,
                cups: couponProduct.cups || [],
                productSpecs: couponProduct.productSpecs || [],
                productAttrs: couponProduct.productAttrs || [],
                modifiedTime: couponProduct.modifiedTime || '', // 保存商品修改时间
                _hasDetail: true
              };
            }
          });
          
          console.log("限制券商品列表处理完成，商品数量：", processedGoodsList.length);
        }

        // 保存所有商品数据用于搜索
        this.allGoodsList = processedGoodsList;
        this.goodsList = processedGoodsList;
        
      } catch (error) {
        console.error("获取商品列表失败:", error);
        this.$toast.fail("获取商品列表失败");
      } finally {
        loading.clear();
      }
    },
    handleItemClick(item) {
      if (item.isSoldOut) {
        this.$toast("该商品已售罄");
        return;
      }
      if (item._loadError) {
        this.$toast.fail("商品信息加载失败，无法查看详情");
        return;
      }
      this.showGoodsDetail(item);
    },
    // 添加一个判断接口成功的方法
    isSuccess(code) {
      return code === 0 || code === "0" || code === "00" || code === "000";
    },
    async showGoodsDetail(item) {
      console.log('点击的商品:', item);
      
      // 检查是否已有详情数据
      if (!item._hasDetail) {
        this.$toast.fail("商品详情数据缺失");
        return;
      }

      // 重置选项
      this.currentGoods = item;
      this.selectedOptions = {};
      this.selectedAttaches = [];

      try {
        // 1. 处理规格列表（杯型）- 菜单接口返回的是 cups 字段
        let finalUnitList = [];
        if (item.cups && item.cups.length > 0) {
          finalUnitList = item.cups.map((cup, index) => ({
            unitId: cup.specId,
            unitName: cup.specName,
            salePrice: cup.specPrice,
            isSelect: index === 0,
          }));
        }

        // 2. 处理做法列表（温度、甜度等）- 菜单接口返回的是 productAttrs 字段
        let sortedPracticeList = [];
        if (item.productAttrs && item.productAttrs.length > 0) {
          sortedPracticeList = item.productAttrs.map((attr) => ({
            practiceName: attr.attributeName,
            practiceId: attr.attributeId,
            isRequired: 1, // 温度、糖度等一般是必选
            practiceValueList: attr.productAttrs.map((opt) => ({
              practiceValueId: opt.attributeId,
              practiceValue: opt.attributeName,
              price: 0, // 温度、糖度等不收费
              isDefault: 0,
            })),
          }));
        }

        // 3. 处理加料列表 - 菜单接口返回的是 productSpecs 字段
        let attachGroups = [];
        if (item.productSpecs && item.productSpecs.length > 0) {
          attachGroups = item.productSpecs.map((spec) => ({
            id: spec.specId,
            name: spec.specName,
            minSelect: spec.minSelect || 0,
            maxSelect: spec.maxSelect || 1,
            attaches: spec.specs.map((s) => ({
              attachGoodsId: s.specId,
              attachGoodsName: s.specName,
              salePrice: s.specPrice || 0,
              inventory: s.specStatus === 1 ? 999 : 0, // 1表示有货
              pictureUrlList: [],
            })),
          }));
        }

        // 4. 转换做法/选项 (如温度、甜度)
        const modifyList = sortedPracticeList.map((group) => ({
          typeName: group.practiceName,
          groupMinChoose: group.isRequired,
          data: group.practiceValueList.map((opt) => ({
            goodsId: opt.practiceValueId,
            goodsName: opt.practiceValue,
            isSelect: opt.isDefault === 1,
          })),
        }));

        // 5. 组合成UI需要的数据结构
        this.goodsDetail = {
          goodsId: item.goodsId,
          goodsName: item.goodsName,
          goodsImg: item.goodsImg,
          type: item.type,
          skuTagDataList: item.skuTagDataList,
          customTag: item.customTag,
          unitList: finalUnitList,
          sortedPracticeList: sortedPracticeList,
          attachGroups: attachGroups,
          modifyList: modifyList,
          goodsSkuList: item.cups || [], // 使用 cups 作为 sku 列表
          modifiedTime: this.currentGoods?.modifiedTime || item.modifiedTime || '', // 保存商品修改时间
        };

        // 6. 设置默认选中的选项
        if (this.goodsDetail.modifyList) {
          this.goodsDetail.modifyList.forEach((group) => {
            const defaultOption =
              group.data.find((opt) => opt.isSelect) || group.data[0];
            if (defaultOption) {
              this.$set(
                this.selectedOptions,
                group.typeName,
                defaultOption.goodsName
              );
            }
          });
        }

        // 7. 默认选中第一个免费底料（如果有的话）
        if (this.goodsDetail.attachGroups) {
          const diLiaoGroup = this.goodsDetail.attachGroups.find(g => g.name === '底料');
          if (diLiaoGroup) {
            const freeAttaches = diLiaoGroup.attaches.filter(a => (!a.salePrice || a.salePrice === 0) && a.inventory > 0);
            if (freeAttaches.length > 0) {
              // 默认选中第一个
              this.selectedAttaches.push({
                attachGoodsId: freeAttaches[0].attachGoodsId,
                attachGoodsName: freeAttaches[0].attachGoodsName,
                attachGoodsPrice: freeAttaches[0].salePrice || 0,
                attachSkuId: freeAttaches[0].attachGoodsId,
                num: 1
              });
            }
          }
        }

        this.showDetail = true;
      } catch (error) {
        console.error("处理商品详情失败:", error);
        this.$toast.fail("打开商品详情失败");
      }
    },
    selectOption(groupName, optionName) {
      this.$set(this.selectedOptions, groupName, optionName);
    },
    // 判断加料是否被选中
    isAttachSelected(attachGoodsId) {
      return this.selectedAttaches.some(a => a.attachGoodsId === attachGoodsId);
    },
    // 切换加料选择状态
    toggleAttach(attach) {
      if (attach.inventory <= 0) {
        this.$toast('该加料已售罄');
        return;
      }

      const index = this.selectedAttaches.findIndex(
        a => a.attachGoodsId === attach.attachGoodsId
      );

      if (index > -1) {
        // 已选中，取消选择
        this.selectedAttaches.splice(index, 1);
      } else {
        // 未选中，添加选择
        this.selectedAttaches.push({
          attachGoodsId: attach.attachGoodsId,
          attachGoodsName: attach.attachGoodsName,
          attachGoodsPrice: attach.salePrice,
          attachSkuId: attach.attachGoodsId, // 根据实际需要调整
          num: 1
        });
      }
    },
    async goToSettlement() {
      if (!this.isValidSelection) {
        this.$toast("请选择必选项");
        return;
      }

      const selectedUnit = this.goodsDetail.unitList.find((u) => u.isSelect);
      
      // 收集选中的属性ID（温度、糖度等）
      const attributeIds = [];
      (this.goodsDetail.sortedPracticeList || []).forEach((group) => {
        const selectedOptionName = this.selectedOptions[group.practiceName];
        if (selectedOptionName) {
          const selectedOption = group.practiceValueList.find(
            (opt) => opt.practiceValue === selectedOptionName
          );
          if (selectedOption) {
            attributeIds.push(selectedOption.practiceValueId);
          }
        }
      });

      // 收集选中的加料ID
      const specIds = this.selectedAttaches.map(attach => attach.attachGoodsId);

      // 构建购物车商品数据
      const product = {
        productId: this.goodsDetail.goodsId,
        cupId: selectedUnit ? selectedUnit.unitId : "",
        modifiedTime: this.goodsDetail.modifiedTime || new Date().toISOString().replace('T', ' ').substring(0, 19),
        productAmount: 1,
        specIds: specIds,
        attributeIds: attributeIds,
        comboProductDetail: []
      };

      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });

      try {
        // 调用购物车结算接口
        const response = await request.post("/mx/update-cart-settlement", {
          shopId: this.$route.query.shopId,
          orderType: '1',
          products: [product],
          takeType: 1,
          plusDiscountProductList: [],
          useWalletPay: 0,
          customerId: this.$store.state.memberCoupon?.customerId || '',
          accessToken: this.$store.state.mxAccessToken,
        });

        loading.clear();

        console.log('购物车结算响应:', response);

        if (response && response.code === 0) {
          // 购物车更新成功，拼装 GoodsDetail 需要的数据格式
          const cartData = response.data || response;
          
          // 拼装成 orderConfirmData 格式
          const orderConfirmData = {
            shopName: this.shopName,
            shopAddress: this.shopAddress,
            goodsInfo: [{
              items: [{
                uniqueId: this.goodsDetail.goodsId,
                image: this.goodsDetail.goodsImg,
                name: this.goodsDetail.goodsName,
                salePrice: (cartData.products && cartData.products[0]) ? (cartData.products[0].productPrice / 100) : 0,
                skuInfo: this.buildSkuInfo()
              }]
            }],
            cartData: cartData // 保存原始购物车数据用于下单
          };
          
          // 清理 undefined 值
          const cleanData = this.removeUndefined(orderConfirmData);
          
          this.$router.push({
            path: "/goods-detail",
            query: {
              shopId: this.$route.query.shopId,
              orderInfo: JSON.stringify(cleanData), // 使用 orderInfo 参数名
            },
          });
        } else {
          this.$toast.fail(response.msg || "加入购物车失败");
        }
      } catch (error) {
        loading.clear();
        console.error("购物车结算失败:", error);
        this.$toast.fail("加入购物车失败");
      }
    },
    // 生成UUID
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    // 格式化日期为 yyyyMMddHHmmss
    formatDate(date) {
      const pad = (num) => (num < 10 ? "0" + num : num);
      return (
        `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
          date.getDate()
        )}` +
        `${pad(date.getHours())}${pad(date.getMinutes())}${pad(
          date.getSeconds()
        )}`
      );
    },
    // 新增：选择规格容量
    selectUnit(unit) {
      // 必须通过创建新数组的方式来触发响应式更新
      this.goodsDetail.unitList = this.goodsDetail.unitList.map((u) => ({
        ...u,
        isSelect: u.unitId === unit.unitId,
      }));
    },
    // 搜索输入处理（防抖）
    onSearchInput() {
      // 清除之前的定时器
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // 设置新的定时器，300ms后执行搜索
      this.searchTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    // 搜索清除处理
    onSearchClear() {
      this.searchKeyword = "";
      this.goodsList = this.allGoodsList;
    },
    // 执行搜索
    performSearch() {
      if (!this.searchKeyword.trim()) {
        // 如果搜索关键词为空，显示所有商品
        this.goodsList = this.allGoodsList;
        return;
      }

      const keyword = this.searchKeyword.toLowerCase().trim();

      // 根据商品名称、标签和描述进行搜索
      this.goodsList = this.allGoodsList.filter((item) => {
        // 搜索商品名称
        const nameMatch =
          item.goodsName && item.goodsName.toLowerCase().includes(keyword);

        // 搜索商品标签
        const tagMatch =
          item.skuTagDataList &&
          item.skuTagDataList.some((tag) =>
            tag.toLowerCase().includes(keyword)
          );

        // 搜索商品描述（去除HTML标签后搜索）
        const descMatch =
          item.customTag &&
          item.customTag
            .replace(/<[^>]*>/g, "")
            .toLowerCase()
            .includes(keyword);

        return nameMatch || tagMatch || descMatch;
      });
    },
    // 构建 skuInfo 对象
    buildSkuInfo() {
      const skuInfo = {};
      
      // 添加杯型
      const selectedUnit = this.goodsDetail.unitList.find((u) => u.isSelect);
      if (selectedUnit) {
        skuInfo['杯型'] = selectedUnit.unitName;
      }
      
      // 添加选中的做法（温度、糖度等）
      (this.goodsDetail.sortedPracticeList || []).forEach((group) => {
        const selectedOptionName = this.selectedOptions[group.practiceName];
        if (selectedOptionName) {
          skuInfo[group.practiceName] = selectedOptionName;
        }
      });
      
      // 添加选中的加料
      if (this.selectedAttaches.length > 0) {
        const attachNames = this.selectedAttaches.map(a => a.attachGoodsName);
        skuInfo['加料'] = attachNames.join('、');
      }
      
      return skuInfo;
    },
    // 递归清理对象中的 undefined 值
    removeUndefined(obj) {
      if (obj === null || obj === undefined) {
        return null;
      }
      
      if (Array.isArray(obj)) {
        return obj.map(item => this.removeUndefined(item));
      }
      
      if (typeof obj === 'object') {
        const cleaned = {};
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = this.removeUndefined(value);
            }
          }
        }
        return cleaned;
      }
      
      return obj;
    },
  },
};
</script>

<style lang="scss" scoped>
.goods-list {
  min-height: 100vh;
  background: #f7f8fa;
}

.search-container {
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;

  :deep(.van-search) {
    padding: 0;
    background: transparent;
  }

  :deep(.van-search__content) {
    background: #f7f8fa;
    border-radius: 18px;
  }

  :deep(.van-field__control) {
    font-size: 14px;
  }
}

.goods-container {
  padding: 12px;
}

.goods-item {
  display: flex;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.goods-image {
  width: 120px;
  height: 120px;
  margin-right: 12px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sold-out-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      color: #fff;
      font-size: 16px;
      padding: 4px 12px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 4px;
    }
  }
}

.goods-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.goods-info {
  .goods-name {
    font-size: 16px;
    font-weight: 500;
    margin: 0 0 8px;
    line-height: 1.4;
  }

  .goods-tags {
    margin-bottom: 8px;

    .tag {
      display: inline-block;
      padding: 2px 6px;
      margin-right: 6px;
      background: #f5f5f5;
      color: #666;
      border-radius: 4px;
      font-size: 12px;
    }
  }

  .goods-desc {
    > span {
      font-size: 12px;
      color: #999;
    }

    font-size: 12px;
    color: #999;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }
}

.goods-action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 8px;

  .add-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &[disabled] {
      opacity: 0.5;
      background: #ccc;
      border-color: #ccc;
    }
  }
}

.detail-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .goods-image {
    width: 100%;
    height: 300px;
    background: #f8f8f8;

    img {
      width: 100%;
      height: 100%;
      object-fit: initial;
    }
  }

  .goods-info {
    padding: 16px;

    .goods-name {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 12px;
    }

    .goods-tags {
      margin-bottom: 12px;

      .tag {
        display: inline-block;
        padding: 4px 8px;
        margin-right: 8px;
        background: #f7f8fa;
        color: #646566;
        border-radius: 16px;
        font-size: 12px;
      }
    }

    .goods-desc {
      font-size: 14px;
      color: #646566;
      line-height: 1.6;
    }
  }
}

.goods-container {
  padding: 0 12px;
}

.specs-section {
  padding: 0 16px 12px;

  .section-title {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 8px;
  }
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .option-item {
    padding: 6px 12px;
    background-color: #f7f8fa;
    border-radius: 16px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid #f7f8fa;
    transition: all 0.2s;

    &.active {
      background-color: #fffbe8;
      border-color: #ffd4b1;
      color: #c58a53;
    }
  }
}

.bottom-bar {
  background-color: #fff;
  padding: 8px 16px;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.05);

  .van-button {
    border-radius: 20px;
  }
}

.unit-list {
  display: flex;
  gap: 12px;
  margin: 12px 0;
}

.unit-item {
  padding: 6px 18px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  background: #fff;
  color: #333;

  &.active {
    border-color: #1989fa;
    color: #1989fa;
    background: #e8f3ff;
  }
}

.attach-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attach-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background-color: #f7f8fa;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #f7f8fa;
  transition: all 0.2s;

  &.active {
    background-color: #fffbe8;
    border-color: #ffd4b1;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .attach-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .attach-image {
      width: 40px;
      height: 40px;
      border-radius: 4px;
      object-fit: cover;
    }

    .attach-name {
      font-size: 14px;
      color: #333;
    }
  }

  .attach-price {
    font-size: 14px;
    color: #ff6b35;
    font-weight: 500;

    .sold-out-text {
      color: #999;
    }
  }
}
</style>